import { useCallback, useState } from 'react';

// Which payment entry is expanded, for a list that allows exactly one.
//
// Payment panels used to own their own open/closed state, so several
// could stand open at once: the page grew very long, and with more than
// one QR on screen there was nothing to say which one a visitor was
// meant to scan. Only the parent list can enforce "one at a time", so
// the state lives here and the rows are told whether they are open.
//
// A hook rather than a context provider: the public profile and the
// owner's dashboard each render their own copy of the list, so a
// provider would have to be added to both anyway, for the same result
// and an extra layer of indirection. A hook keeps the rule itself in one
// place, which is the part worth not duplicating.
//
// Deliberately holds a key rather than a boolean per row. Anything that
// tracked "is row N open" separately could represent two rows being open
// at once, which is exactly the state this exists to make impossible.
//
// The key is whatever the caller already uses for React's key --
// entry._id, falling back to platform-index -- so there is no second
// identity scheme to keep in step with the first.
export default function usePaymentAccordion() {
  const [openKey, setOpenKey] = useState(null);

  // Toggling the row that is already open closes it, which is what lets
  // a visitor collapse everything rather than being forced to leave one
  // panel open forever. Toggling any other row replaces the open one, so
  // opening and closing are the same gesture and no row has to know
  // about any other.
  const toggle = useCallback((key) => {
    setOpenKey((prev) => (prev === key ? null : key));
  }, []);

  const isOpen = useCallback((key) => openKey === key, [openKey]);

  return { openKey, isOpen, toggle };
}
